import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import ODataTable from 'components/reusable/ODataTable'

const ProjectsTable = ({ data, loading }) => {
  const navigate = useNavigate()

  const statusMap = useMemo(() => ({
    available: 'Available',
    'sold-out': 'Sold Out',
    booked: 'Booked',
    rented: 'Rented',
    'coming-soon': 'Coming Soon'
  }), [])

  const handleNavigateToUnits = useCallback((project, statusKey) => {
    navigate('/units', {
      state: {
        unitFilters: {
          status: statusMap[statusKey] || '',
          searchKey: project.projectTitle || ''
        }
      }
    })
  }, [navigate, statusMap])

  const renderCountCell = useCallback((statusKey) => (project) => {
    const count = project?.byStatus?.[statusKey]?.count || 0
    return (
      <div className="flex items-center justify-between text-xs">
        {count > 0 ? (
          <button
            type="button"
            onClick={() => handleNavigateToUnits(project, statusKey)}
            className="font-medium text-LightBlue hover:underline"
          >
            {count}
          </button>
        ) : (
          <span className="font-medium">{count}</span>
        )}
      </div>
    )
  }, [handleNavigateToUnits])

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Projects Report</h3>
      </div>

      <ODataTable
        data={data?.projects || []}
        columns={[
          {
            key: 'projectTitle',
            title: 'Project Name',
            sortable: false,
            render: (project) => (
              <div className="font-medium text-gray-900">
                {project.projectTitle}
              </div>
            )
          },
          {
            key: 'projectStatus',
            title: 'Status',
            sortable: false,
            render: (project) => (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                project.projectStatus === 'ready' 
                  ? 'bg-green-100 text-green-800' 
                  : project.projectStatus === 'under-construction'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {project.projectStatus}
              </span>
            )
          },
          { key: 'available', title: 'Available', sortable: false, render: renderCountCell('available') },
          { key: 'booked', title: 'Booked', sortable: false, render: renderCountCell('booked') },
          { key: 'sold', title: 'Sold', sortable: false, render: renderCountCell('sold-out') },
          { key: 'coming-soon', title: 'Coming Soon', sortable: false, render: renderCountCell('coming-soon') },
          { key: 'rented', title: 'Rented', sortable: false, render: renderCountCell('rented') },
          {
            key: 'totals',
            title: 'Total Units',
            sortable: false,
            render: (project) => (
              <span className="font-semibold text-gray-900 text-center block">
                {project.totals || 0}
              </span>
            )
          },
          {
            key: 'totalPrice',
            title: 'Total Value',
            sortable: false,
            render: (project) => {
              const value = Number(project.totalPrice ?? 0)
              const formatted = Number.isFinite(value) ? value.toLocaleString() : project.totalPrice
              return (
                <span className="font-semibold text-gray-900">
                  {formatted}
                </span>
              )
            }
          }
        ]}
        loading={loading}
        pagination={data?.pagination}
        emptyMessage="No records found"
      />
    </div>
  )
}

export default ProjectsTable


